/**
 * Small formatting helpers. Kept dependency-free (no date lib) to keep the
 * bundle lean for the field tablet.
 */

/**
 * Parse a timestamp the API produced.
 *
 * The backend serialises naive-UTC datetimes with `.isoformat()`, which emits no timezone
 * designator ("2026-09-11T00:39:29"). JS `new Date()` treats a bare datetime as LOCAL time,
 * so in MDT every UTC timestamp was read six hours in the future and relTime returned a
 * negative age -- rendering "just now" for the entire fleet.
 *
 * That bug was invisible until now only because last_seen was itself always ~now; making the
 * field truthful is what exposed it. Append the Z ourselves when the string carries no
 * offset, so a value that IS zoned (or already ends in Z) is left alone.
 */
export function parseApiTime(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const zoned = /(?:Z|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`;
  const t = new Date(zoned).getTime();
  return Number.isNaN(t) ? null : t;
}

/** Relative "time ago" from an ISO timestamp, e.g. "3m ago" / "just now". */
export function relTime(iso: string | null | undefined): string {
  if (!iso) return "never";
  const then = parseApiTime(iso);
  if (then === null) return "unknown";
  const secs = Math.round((Date.now() - then) / 1000);
  if (secs < 45) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

/** Signal-strength label from RSSI dBm (best-effort, coarse buckets). */
export function rssiLabel(rssi: number | null | undefined): string {
  if (rssi == null) return "—";
  if (rssi >= -60) return `${rssi} dBm (strong)`;
  if (rssi >= -72) return `${rssi} dBm (ok)`;
  return `${rssi} dBm (weak)`;
}

/** Absolute local-time rendering of an API timestamp, for tooltips. */
export function fmtAbsolute(iso: string | null | undefined): string {
  const t = parseApiTime(iso);
  return t === null ? "unknown" : new Date(t).toLocaleString();
}

/** Absolute local DATE (no time) rendering of an API timestamp. Routes through parseApiTime so
 * a naive-UTC server date is not misread as local (which can shift it a whole day). */
export function fmtDate(iso: string | null | undefined): string {
  const t = parseApiTime(iso);
  return t === null ? "—" : new Date(t).toLocaleDateString();
}

/**
 * Byte count in the unit a human reads, not raw digits: "—" when absent, "0 B" for zero,
 * otherwise B/KB/GB/… to one decimal (dropped once the value is >= 10, which reads cleaner).
 *
 * Consolidated from the near-duplicate helpers that lived inline in Clients.tsx and
 * ClientUsage.tsx so the client surfaces (list, usage table, location sheet) all format
 * identically.
 */
export function fmtBytes(n?: number | null): string {
  if (n == null) return "—";
  if (n === 0) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let v = n, i = 0;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(i === 0 || v >= 10 ? 0 : 1)} ${u[i]}`;
}

/** bits/sec -> Mbps/kbps/bps. "—" when absent. The ranked usage metric, so it keeps a decimal. */
export function fmtRate(bps?: number | null): string {
  if (bps == null) return "—";
  if (bps >= 1_000_000) return `${(bps / 1_000_000).toFixed(2)} Mbps`;
  if (bps >= 1_000) return `${(bps / 1_000).toFixed(1)} kbps`;
  return `${bps.toFixed(0)} bps`;
}

/**
 * Human duration from a second count: "45s" / "12m" / "3h 5m" / "2d 4h". "—" when absent.
 * Sub-minute granularity (the "45s" case) is a superset of the old fmtDur helpers, which
 * bottomed out at "0m"; every longer range renders identically to before.
 */
export function fmtDuration(s?: number | null): string {
  if (s == null) return "—";
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  const h = Math.floor(s / 3600);
  return h < 24 ? `${h}h ${Math.floor((s % 3600) / 60)}m` : `${Math.floor(h / 24)}d ${h % 24}h`;
}
